import React, { useState, useEffect } from 'react';
import useOutsideAlerter from 'react-scale/components/audits/SpeedAudit/v2/hooks/useOutsideAlerter';
import { useAuditErrorCategories } from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import CreatableSelect from 'react-select-3/creatable';
import { OptionsType, OptionTypeBase, ValueType } from 'react-select-3/src/types';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface CreatableSelectOptionType extends OptionTypeBase {
  value: string;
  label: string;
}

interface ChatBulkAuditErrorPickerProps {
  projectId: string;
  selectedErrorCategories: string[];
  enableCreateNewError: boolean;
  toggleChoose: (errorCategory: string[], setVsFilter?: boolean) => void;
  isDisabled?: boolean;
}

const stringArrayToSelectArray = (values: string[]): OptionsType<CreatableSelectOptionType> => {
  return values.map(value => ({ label: value, value }));
};

const ChatBulkAuditErrorPicker: React.FC<ChatBulkAuditErrorPickerProps> = ({
  projectId,
  selectedErrorCategories,
  enableCreateNewError = false,
  toggleChoose,
  isDisabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  useOutsideAlerter(wrapperRef, () => setOpen(false));
  const displayStandardizedErrorCategories = useUnleashFeatureFlag(
    'enable-standardized-error-categories',
    { projectId },
    false,
  );
  const { data: possibleErrorCategories, error } = useAuditErrorCategories(
    projectId,
    displayStandardizedErrorCategories,
  );

  useEffect(() => {
    if (error) {
      setErrorMessage('Error fetching error categories');
    } else {
      setErrorMessage('');
    }
  }, [error]);

  const options = stringArrayToSelectArray(possibleErrorCategories ?? []);
  const selectedOptions = stringArrayToSelectArray(selectedErrorCategories);

  const errorPickerClassname = 'error-picker' + (open ? ' error-picker--open' : '');

  // TODO fix this, but I can't find another way to get this input element in select
  document
    .getElementById('react-select-input')
    ?.style.setProperty('box-shadow', 'none', 'important');

  return (
    <div className={errorPickerClassname} ref={wrapperRef}>
      <style jsx>{`
        .error-picker {
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .select-head {
          border: 1px solid var(--color-LightSkyBlue);
          padding: 7px 16px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .select-head:hover {
          background: #eee;
        }

        .error-picker.error-picker--open .select-head {
          color: var(--color-primary);
          border: 1px solid var(--color-primary);
        }

        .opts-wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
          min-width: 200px;
          border-radius: 8px;
        }

        .opts-wrapper:hover {
          cursor: pointer;
        }

        .choose-error-category {
          padding: 6px 10px;
        }

        .choose-error-category:hover {
          background: #eee;
          cursor: pointer;
        }

        .choose-error-category.choose-error-category--on {
          background: #eee;
          border-bottom: 2px solid var(--color-primary);
        }
      `}</style>
      <div className="opts-wrapper">
        <div>
          <CreatableSelect
            fullWidth
            placeholder="Error type"
            isMulti={true}
            isDisabled={isDisabled}
            options={options}
            value={selectedOptions}
            isValidNewOption={() => enableCreateNewError}
            menuMaxHeight={80}
            onCreateOption={(_: string) => {
              // TODO: allow creating new error category for component reusability
              return;
            }}
            inputId="react-select-input"
            onChange={(newValue: ValueType<CreatableSelectOptionType>) => {
              const selectedString: string[] =
                newValue?.map((element: CreatableSelectOptionType) => element.value) ?? [];
              toggleChoose(selectedString, true);
            }}
          />
          {errorMessage}
        </div>
      </div>
    </div>
  );
};

export default ChatBulkAuditErrorPicker;
